import React from "react";
import { Link } from "react-router-dom";
import { useT } from "../context/LanguageContext";
import { Lock, ChevronRight, Trophy } from "lucide-react";
import ActiveLeagues from "../components/ActiveLeagues";
import V2Banner from "../components/V2Banner";
import CtfBanner from "../components/CtfBanner";

// 6 game-league tiles. The 3 live ones link to their respective dashboards.
// Titles/subtitles come from translations; static metadata (image, href, accent) stays here.
const LEAGUES = [
  {
    id: "wc2026",
    titleKey: "home.tile.wc2026Title",
    subKey: "home.tile.wc2026Sub",
    cover: "/wc/wc-2026-main.jpg",
    coverFallback: "/wc/wc-2026-main.jpg",
    href: "/wc2026",
    badgeKey: "home.wc2026Badge",
    active: true,
    accent: "#FFD700",
  },
  {
    id: "efootball-mobile-oml",
    titleKey: "home.tile.mobileTitle",
    subKey: "home.tile.mobileSub",
    cover: "/oml-mobile-cover.webp",
    coverFallback: "/oml-mobile-cover.jpg",
    href: "/dashboard?platform=mobile",
    badgeKey: "home.mobileBadge",
    active: true,
    accent: "#EA580C",
  },
  {
    id: "efootball-console-oml",
    titleKey: "home.tile.consoleTitle",
    subKey: "home.tile.consoleSub",
    cover: "/oml-mobile-cover.webp",
    coverFallback: "/oml-mobile-cover.jpg",
    href: "/dashboard?platform=console",
    badgeKey: "home.consoleBadge",
    active: true,
    accent: "#7C3AED",
  },
  {
    id: "byol",
    titleKey: "home.tile.byolTitle",
    subKey: "home.tile.byolSub",
    cover: "/byol-cover.png",
    coverFallback: "/byol-cover.png",
    href: "/byol",
    badgeKey: "home.private",
    active: true,
    accent: "#7C3AED",
  },
  {
    id: "ufl-mobile",
    titleKey: "home.tile.uflTitle",
    subKey: "home.tile.uflSub",
    cover: "/wc/ufl-mobile.png",
    coverFallback: "/wc/ufl-mobile.png",
    href: "/v2/ufl/mobile",
    badgeKey: "home.uflBadge",
    active: true,
    accent: "#60A5FA",
  },
  {
    id: "classics",
    titleKey: "home.tile.classicsTitle",
    subKey: "home.tile.classicsSub",
    cover: "/classics-homepage.jpg",
    coverFallback: "/classics-homepage.jpg",
    href: "/classics",
    badgeKey: "home.pcOnly",
    active: true,
    accent: "#00FF87",
  },
];

export default function Home() {
  const { t } = useT();
  const live = LEAGUES.filter((l) => l.active).length;
  const soon = LEAGUES.length - live;

  return (
    <div data-testid="home-page" className="space-y-8">
      <V2Banner />
      <CtfBanner />
      <header className="flex items-end justify-between flex-wrap gap-4" data-testid="home-header">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">{t("home.kicker")}</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-wider leading-none mt-2">
            {t("home.title1")} <span className="text-[#7C3AED]">{t("home.title2")}</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm text-[#71717A] font-body">
            {t("home.intro")}
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2" data-testid="home-count">
          <Trophy className="inline w-3 h-3 mr-2 text-[#EA580C]" />
          {t("home.count", { n: live, m: soon })}
        </div>
      </header>

      <ActiveLeagues />

      <section className="space-y-4" data-testid="home-leagues">
        {LEAGUES.map((l) => {
          if (!l.active) return <LockedTile key={l.id} l={l} />;
          if (l.id === "wc2026") return <Wc2026Tile key={l.id} />;
          return <ActiveTile key={l.id} l={l} />;
        })}
      </section>
    </div>
  );
}

function Wc2026Tile() {
  const { t } = useT();
  return (
    <Link
      to="/wc2026"
      data-testid="home-tile-wc2026"
      className="group relative block overflow-hidden rounded-2xl bg-[#020617] border border-[#FFD700]/40 hover:border-[#FFD700] transition-all duration-300 hover:shadow-[0_0_50px_rgba(255,215,0,0.3)]"
    >
      {/* Ambient host-color orbs (Mexico red · gold · Canada/Mexico green) */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-16 w-72 h-72 rounded-full bg-[#B22234]/25 blur-[120px]" />
        <div className="absolute -bottom-24 -right-12 w-72 h-72 rounded-full bg-[#006847]/25 blur-[120px]" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-80 h-48 rounded-full bg-[#FFD700]/15 blur-[130px]" />
      </div>
      {/* faint grid texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: "linear-gradient(rgba(255,215,0,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,1) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* "3 Modes" badge */}
      <span className="absolute top-3 right-3 z-10 px-2.5 py-0.5 rounded-full bg-[#FFD700] text-[#020617] text-[9px] font-extrabold uppercase tracking-[0.25em]">
        {t("home.wc2026Badge")}
      </span>

      <div className="relative px-5 sm:px-7 py-7 sm:py-9 flex items-center gap-4 sm:gap-6">
        {/* Trophy emblem */}
        <div className="relative shrink-0">
          <div aria-hidden className="absolute inset-0 -m-3 rounded-full bg-[#FFD700]/25 blur-2xl" />
          <img
            src="/wc/wc2026-dark.png"
            alt=""
            loading="eager"
            decoding="async"
            className="relative h-20 sm:h-28 w-auto object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.6)] group-hover:scale-105 transition-transform duration-500"
            draggable={false}
          />
        </div>

        {/* Title block */}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFD700]/80">// 3 modes · 1 trophy</div>
          <h2 className="font-heading uppercase leading-[0.88] mt-1.5 drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
            <span
              className="block text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg,#B22234 0%,#ffffff 52%,#006847 100%)", fontSize: "clamp(18px,3.6vw,30px)" }}
            >
              FIFA WORLD CUP
            </span>
            <span
              className="block font-extrabold text-[#FFD700]"
              style={{ fontSize: "clamp(38px,8.5vw,68px)", textShadow: "0 0 42px rgba(255,215,0,0.45)" }}
            >
              2026
            </span>
          </h2>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-lg">🇲🇽</span><span className="text-lg">🇺🇸</span><span className="text-lg">🇨🇦</span>
            <span className="text-[11px] sm:text-[12px] text-white/70">{t("home.tile.wc2026Sub")}</span>
          </div>
          <span className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-[0.3em] bg-[#FFD700] text-[#020617] group-hover:gap-2.5 transition-all w-fit">
            {t("wc26.enter")} <ChevronRight className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function ActiveTile({ l }) {
  const { t } = useT();
  const accent = l.accent || "#EA580C";
  const btnHover = accent === "#7C3AED" ? "#6D28D9" : "#C2410C";
  const title = t(l.titleKey);
  const subtitle = t(l.subKey);
  return (
    <Link
      to={l.href}
      data-testid={`home-tile-${l.id}`}
      className="group relative block overflow-hidden bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#7C3AED] transition-all duration-300 hover:shadow-[0_0_40px_rgba(124,58,237,0.2)]"
    >
      <div className="relative w-full aspect-[16/7] sm:aspect-[21/7] overflow-hidden">
        <picture>
          <source srcSet={l.cover} type="image/webp" />
          <img
            src={l.coverFallback || l.cover}
            alt={`${title} — ${subtitle}`}
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            data-testid={`home-tile-cover-${l.id}`}
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/20 to-transparent sm:from-black/70" aria-hidden />
        <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-8">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-2 bg-[#34C759] text-white font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 shadow-[0_4px_12px_rgba(52,199,89,0.35)]"
              data-testid={`home-tile-live-${l.id}`}
            >
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              {t("home.live")}
            </span>
            {l.badgeKey && (
              <span
                className="inline-flex items-center bg-black/50 border border-white/30 text-white font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 backdrop-blur-sm"
                data-testid={`home-tile-badge-${l.id}`}
              >
                {t(l.badgeKey)}
              </span>
            )}
          </div>
          <div>
            <div
              className="font-mono text-[10px] uppercase tracking-[0.3em] drop-shadow-[0_0_6px_rgba(255,212,0,0.5)]"
              style={{ color: accent }}
            >
              {title}
            </div>
            <div className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wider text-white mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
              {subtitle}
            </div>
            <div
              className="mt-4 inline-flex items-center gap-2 font-heading text-sm uppercase tracking-[0.3em] text-white px-5 py-2.5 transition-colors shadow-[0_8px_20px_rgba(0,0,0,0.3)]"
              style={{ backgroundColor: accent }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = btnHover)}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = accent)}
            >
              {t("home.enterLeague")} <ChevronRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function LockedTile({ l }) {
  const { t } = useT();
  return (
    <div
      data-testid={`home-tile-${l.id}`}
      className="relative overflow-hidden bg-[#F4F4F5] dark:bg-[#232329] border border-[#E4E4E7] dark:border-[#2A2A30] px-5 sm:px-8 py-6 sm:py-8 flex items-center justify-between gap-4 flex-wrap opacity-70"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg,#ffffff 0,#ffffff 1px,transparent 1px,transparent 14px)",
        }}
        aria-hidden
      />
      <div className="relative">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
          {t(l.titleKey)}
        </div>
        <div className="font-heading text-xl sm:text-2xl uppercase tracking-wider text-[#09090B]/80 mt-1">
          {t(l.subKey)}
        </div>
      </div>
      <div
        className="relative inline-flex items-center gap-2 border border-[#D4D4D8] dark:border-[#3F3F46] text-[#71717A] font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-2"
        data-testid={`home-tile-coming-${l.id}`}
      >
        <Lock className="w-3 h-3" />
        {t("home.comingSoon")}
      </div>
    </div>
  );
}
